import { CampaignSend } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import {
    ABTestConfig,
    CampaignChannels,
    CampaignGraph,
    CampaignStructureItem,
    GraphEdge,
    GraphNode,
    Send,
    SplitActions,
    SplitTypes,
} from 'domains/campaigns/types';
import { SegmentDefinition, SegmentEngagement } from 'domains/segments/types';

// ---------------------------------------------------------------------------
// Broad input type — covers all fields from both CampaignSend and CampaignStructureItem
// so the normalization step can access any field without `as any` casts.
// ---------------------------------------------------------------------------

type RawSendInput = {
    id?: number;
    parentId?: number;
    parentIndex?: number;
    sendIndex?: number;
    channel: CampaignChannels;
    splitType?: SplitTypes;
    splitAction?: SplitActions;
    parentAudienceDelayInterval?: string;
    segmentId?: number;
    cohortId?: string;
    name?: string;
    templateId?: number;
    segmentDefinition?: SegmentDefinition;
    segments?: SegmentEngagement[];
};

// ---------------------------------------------------------------------------
// Internal normalized send — uniform shape used throughout graph construction
// ---------------------------------------------------------------------------

type InternalSend = RawSendInput & {
    _index: number;
};

type BuildContext = {
    nodes: GraphNode[];
    edges: GraphEdge[];
    visitedNodeIds: Set<string>;
    allSends: InternalSend[];
    fullSends?: Send[];
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const resolveNodeId = (
    send: InternalSend,
    nodeType: 'send' | 'time_delay' | 'conditional_split' | 'exit' | 'random_split'
): string => {
    const key = send.id != null ? `${send.id}` : `${send._index}`;
    switch (nodeType) {
        case 'send':
            return `send-${key}`;
        case 'time_delay':
            return `delay-${key}`;
        case 'conditional_split':
            return `split-${key}`;
        case 'exit':
            return `exit-${key}`;
        case 'random_split':
            return `random-split-${key}`;
    }
};

export const findDirectChildren = (send: InternalSend, allSends: InternalSend[]): InternalSend[] => {
    if (send.id != null) {
        return allSends.filter((s) => s.parentId === send.id);
    }
    return allSends.filter((s) => s.parentIndex === send._index);
};

const findDoesNotMeetSibling = (meetsSend: InternalSend, allSends: InternalSend[]): InternalSend | undefined => {
    if (meetsSend.id != null) {
        return allSends.find((s) => s.parentId === meetsSend.parentId && s.splitType === SplitTypes.DOES_NOT_MEET);
    }
    return allSends.find((s) => s.parentIndex === meetsSend.parentIndex && s.splitType === SplitTypes.DOES_NOT_MEET);
};

const isExitSend = (send: InternalSend): boolean =>
    send.splitAction === SplitActions.EXIT || send.channel === CampaignChannels.NOOP;

const findParentSend = (send: InternalSend, allSends: InternalSend[]): InternalSend | undefined => {
    if (send.id != null && send.parentId != null) {
        return allSends.find((s) => s.id === send.parentId);
    }
    if (send.parentIndex != null) {
        return allSends.find((s) => s._index === send.parentIndex);
    }
    return undefined;
};

/**
 * Returns true only when a send is the ROOT of a conditional split branch — i.e. it has a
 * splitType but its parent does NOT.  Sends that merely inherited splitType from the reducer
 * while being deeper in the branch are NOT branch roots and should be treated as regular
 * chained sends.
 */
const isSplitBranchRoot = (send: InternalSend, allSends: InternalSend[]): boolean => {
    if (!send.splitType) return false;
    const parent = findParentSend(send, allSends);
    return !parent?.splitType;
};

/**
 * Returns true when a send is the GROUP_A root of a random split — it has
 * RANDOM_GROUP_1 splitType and its parent does NOT share that split type.
 */
const isRandomSplitBranchRoot = (send: InternalSend, allSends: InternalSend[]): boolean => {
    if (send.splitType !== SplitTypes.RANDOM_GROUP_1) return false;
    const parent = findParentSend(send, allSends);
    return !parent?.splitType;
};

const findGroupBSibling = (groupASend: InternalSend, allSends: InternalSend[]): InternalSend | undefined => {
    if (groupASend.id != null) {
        return allSends.find((s) => s.parentId === groupASend.parentId && s.splitType === SplitTypes.RANDOM_GROUP_2);
    }
    return allSends.find((s) => s.parentIndex === groupASend.parentIndex && s.splitType === SplitTypes.RANDOM_GROUP_2);
};

// ---------------------------------------------------------------------------
// Recursive graph processor
// ---------------------------------------------------------------------------

const processSend = (send: InternalSend, parentGraphNodeId: string, context: BuildContext): void => {
    const { nodes, edges, visitedNodeIds, allSends, fullSends } = context;

    const addNode = (node: GraphNode) => {
        if (!visitedNodeIds.has(node.id)) {
            nodes.push(node);
            visitedNodeIds.add(node.id);
        }
    };

    const addEdge = (edge: GraphEdge) => {
        edges.push(edge);
    };

    const isRoot = send.parentIndex == null && send.parentId == null;
    // Only treat as branch root when the parent has no splitType — sends that inherited
    // splitType from the reducer while being deeper in the branch are regular chained sends.
    const isMeets = send.splitType === SplitTypes.MEETS && isSplitBranchRoot(send, allSends);
    const isDoesMeet = send.splitType === SplitTypes.DOES_NOT_MEET && isSplitBranchRoot(send, allSends);
    const isRandomGroupA = isRandomSplitBranchRoot(send, allSends);
    const isRandomGroupB = send.splitType === SplitTypes.RANDOM_GROUP_2 && isSplitBranchRoot(send, allSends);
    const sendNodeId = resolveNodeId(send, 'send');
    const navIndex = send.sendIndex ?? send._index;

    // DOES_NOT_MEET sends are fully handled inside their MEETS sibling's processing
    if (isDoesMeet && !isRoot) return;

    // RANDOM_GROUP_2 sends are fully handled inside GROUP_A sibling's processing
    if (isRandomGroupB && !isRoot) return;

    // Merge node guard: if this send node was already visited, just add the new incoming edge
    if (!isRoot && visitedNodeIds.has(sendNodeId)) {
        addEdge({ id: `e-${parentGraphNodeId}-${sendNodeId}`, source: parentGraphNodeId, target: sendNodeId });
        return;
    }

    // `thisSendNodeId` tracks the graph node created for this send (null if exit)
    // It is extracted into a const after the nullable assignment to enable TypeScript
    // to narrow the type in the closure passed to forEach below.
    let thisSendNodeId: string | null = null;

    // -----------------------------------------------------------------------
    // Root send (no parent)
    // -----------------------------------------------------------------------
    if (isRoot) {
        if (isExitSend(send)) {
            const exitNodeId = resolveNodeId(send, 'exit');
            addNode({ id: exitNodeId, type: 'exit', data: null });
            addEdge({ id: `e-${parentGraphNodeId}-${exitNodeId}`, source: parentGraphNodeId, target: exitNodeId });
        } else {
            addNode({
                id: sendNodeId,
                type: 'send',
                data: {
                    sendId: send.id,
                    sendIndex: navIndex,
                    channel: send.channel,
                    sendName: send.name,
                    templateId: send.templateId,
                    splitType: send.splitType,
                    splitAction: send.splitAction,
                },
            });
            addEdge({ id: `e-${parentGraphNodeId}-${sendNodeId}`, source: parentGraphNodeId, target: sendNodeId });
            thisSendNodeId = sendNodeId;
        }
    }
    // -----------------------------------------------------------------------
    // Non-root send — always preceded by a time delay node
    // -----------------------------------------------------------------------
    else {
        const delayNodeId = resolveNodeId(send, 'time_delay');
        addNode({
            id: delayNodeId,
            type: 'time_delay',
            data: { interval: send.parentAudienceDelayInterval, sendIndex: navIndex },
        });
        addEdge({ id: `e-${parentGraphNodeId}-${delayNodeId}`, source: parentGraphNodeId, target: delayNodeId });

        if (isRandomGroupA) {
            // ----------------------------------------------------------------
            // RANDOM_GROUP_1 send — opens a random split node
            // ----------------------------------------------------------------
            const randomSplitNodeId = resolveNodeId(send, 'random_split');
            addNode({ id: randomSplitNodeId, type: 'random_split', data: { sendIndex: navIndex } });
            addEdge({ id: `e-${delayNodeId}-${randomSplitNodeId}`, source: delayNodeId, target: randomSplitNodeId });

            // Group 1 branch (this send — RANDOM_GROUP_1)
            if (!isExitSend(send)) {
                addNode({
                    id: sendNodeId,
                    type: 'send',
                    data: {
                        sendId: send.id,
                        sendIndex: navIndex,
                        channel: send.channel,
                        sendName: send.name,
                        templateId: send.templateId,
                        splitType: send.splitType,
                        splitAction: send.splitAction,
                    },
                });
                addEdge({
                    id: `e-${randomSplitNodeId}-${sendNodeId}`,
                    source: randomSplitNodeId,
                    target: sendNodeId,
                    label: 'group_1',
                });

                findDirectChildren(send, allSends)
                    .filter(
                        (child) =>
                            !(child.splitType === SplitTypes.RANDOM_GROUP_2 && isSplitBranchRoot(child, allSends))
                    )
                    .forEach((child) => processSend(child, sendNodeId, context));
            }

            // Group 2 branch (sibling GROUP_B send)
            const groupBSend = findGroupBSibling(send, allSends);
            if (groupBSend) {
                const groupBNavIndex = groupBSend.sendIndex ?? groupBSend._index;
                const groupBSendNodeId = resolveNodeId(groupBSend, 'send');

                if (!isExitSend(groupBSend)) {
                    addNode({
                        id: groupBSendNodeId,
                        type: 'send',
                        data: {
                            sendId: groupBSend.id,
                            sendIndex: groupBNavIndex,
                            channel: groupBSend.channel,
                            sendName: groupBSend.name,
                            templateId: groupBSend.templateId,
                            splitType: groupBSend.splitType,
                            splitAction: groupBSend.splitAction,
                        },
                    });
                    addEdge({
                        id: `e-${randomSplitNodeId}-${groupBSendNodeId}`,
                        source: randomSplitNodeId,
                        target: groupBSendNodeId,
                        label: 'group_2',
                    });

                    findDirectChildren(groupBSend, allSends)
                        .filter(
                            (child) =>
                                !(child.splitType === SplitTypes.RANDOM_GROUP_2 && isSplitBranchRoot(child, allSends))
                        )
                        .forEach((child) => processSend(child, groupBSendNodeId, context));
                }
            }
        } else if (isMeets) {
            // ----------------------------------------------------------------
            // MEETS send — opens a conditional split
            // ----------------------------------------------------------------
            const splitNodeId = resolveNodeId(send, 'conditional_split');

            // segmentDefinition lives on the send itself (draft) or needs to be
            // fetched from fullSends (published — structure items are topology-only)
            let segmentDefinition: SegmentDefinition | undefined = send.segmentDefinition;
            let segments: SegmentEngagement[] | undefined = send.segments;
            let splitSegmentId: number | undefined = send.segmentId;

            if (fullSends && send.id != null) {
                const fullSend = fullSends.find((s) => s.id === send.id);
                if (fullSend) {
                    segmentDefinition = segmentDefinition ?? fullSend.segmentDefinition;
                    segments = segments ?? fullSend.segments;
                    splitSegmentId = splitSegmentId ?? fullSend.segmentId;
                }
            }

            addNode({
                id: splitNodeId,
                type: 'conditional_split',
                data: { segmentDefinition, segments, splitSegmentId, sendIndex: navIndex },
            });
            addEdge({ id: `e-${delayNodeId}-${splitNodeId}`, source: delayNodeId, target: splitNodeId });

            // MEETS branch (this send)
            if (isExitSend(send)) {
                const exitNodeId = resolveNodeId(send, 'exit');
                addNode({ id: exitNodeId, type: 'exit', data: { splitType: SplitTypes.MEETS } });
                addEdge({
                    id: `e-${splitNodeId}-${exitNodeId}`,
                    source: splitNodeId,
                    target: exitNodeId,
                    label: 'meets',
                });
            } else {
                addNode({
                    id: sendNodeId,
                    type: 'send',
                    data: {
                        sendId: send.id,
                        sendIndex: navIndex,
                        channel: send.channel,
                        sendName: send.name,
                        templateId: send.templateId,
                        splitType: send.splitType,
                        splitAction: send.splitAction,
                    },
                });
                addEdge({
                    id: `e-${splitNodeId}-${sendNodeId}`,
                    source: splitNodeId,
                    target: sendNodeId,
                    label: 'meets',
                });
                thisSendNodeId = sendNodeId;

                // Recurse into MEETS children immediately so they appear in order
                // BEFORE the DOES_NOT_MEET branch nodes in the flat list.
                const meetsBranchParentId = sendNodeId;
                findDirectChildren(send, allSends)
                    .filter(
                        (child) => !(child.splitType === SplitTypes.DOES_NOT_MEET && isSplitBranchRoot(child, allSends))
                    )
                    .forEach((child) => processSend(child, meetsBranchParentId, context));

                // Prevent the generic end-of-function recursion from re-processing these children.
                thisSendNodeId = null;
            }

            // DOES_NOT_MEET branch (sibling send) — rendered after all MEETS children
            const dnmSend = findDoesNotMeetSibling(send, allSends);
            if (dnmSend) {
                const dnmSendNodeId = resolveNodeId(dnmSend, 'send');
                const dnmNavIndex = dnmSend.sendIndex ?? dnmSend._index;

                if (isExitSend(dnmSend)) {
                    const dnmExitId = resolveNodeId(dnmSend, 'exit');
                    addNode({ id: dnmExitId, type: 'exit', data: { splitType: SplitTypes.DOES_NOT_MEET } });
                    addEdge({
                        id: `e-${splitNodeId}-${dnmExitId}`,
                        source: splitNodeId,
                        target: dnmExitId,
                        label: 'does_not_meet',
                    });
                } else {
                    addNode({
                        id: dnmSendNodeId,
                        type: 'send',
                        data: {
                            sendId: dnmSend.id,
                            sendIndex: dnmNavIndex,
                            channel: dnmSend.channel,
                            sendName: dnmSend.name,
                            templateId: dnmSend.templateId,
                            splitType: dnmSend.splitType,
                            splitAction: dnmSend.splitAction,
                        },
                    });
                    addEdge({
                        id: `e-${splitNodeId}-${dnmSendNodeId}`,
                        source: splitNodeId,
                        target: dnmSendNodeId,
                        label: 'does_not_meet',
                    });

                    // Recurse into DOES_NOT_MEET's subtree
                    findDirectChildren(dnmSend, allSends)
                        .filter(
                            (child) =>
                                !(child.splitType === SplitTypes.DOES_NOT_MEET && isSplitBranchRoot(child, allSends))
                        )
                        .forEach((child) => processSend(child, dnmSendNodeId, context));
                }
            }
        }
        // ----------------------------------------------------------------
        // Regular chained send (no split)
        // ----------------------------------------------------------------
        else {
            if (isExitSend(send)) {
                const exitNodeId = resolveNodeId(send, 'exit');
                addNode({ id: exitNodeId, type: 'exit', data: null });
                addEdge({ id: `e-${delayNodeId}-${exitNodeId}`, source: delayNodeId, target: exitNodeId });
            } else {
                addNode({
                    id: sendNodeId,
                    type: 'send',
                    // No splitType here — only direct branch-root sends (immediate MEETS/DNM
                    // children of the conditional_split node) carry the heading label.
                    data: {
                        sendId: send.id,
                        sendIndex: navIndex,
                        channel: send.channel,
                        sendName: send.name,
                        templateId: send.templateId,
                        splitAction: send.splitAction,
                    },
                });
                addEdge({ id: `e-${delayNodeId}-${sendNodeId}`, source: delayNodeId, target: sendNodeId });
                thisSendNodeId = sendNodeId;
            }
        }
    }

    // Recurse into this send's direct children, skipping DOES_NOT_MEET and RANDOM_GROUP_2
    // (already handled above inside their sibling's processing block).
    // Extract nodeId into a const so TypeScript can narrow the closure type correctly.
    if (thisSendNodeId != null) {
        const nodeId = thisSendNodeId;
        findDirectChildren(send, allSends)
            .filter(
                (child) =>
                    !(child.splitType === SplitTypes.DOES_NOT_MEET && isSplitBranchRoot(child, allSends)) &&
                    !(child.splitType === SplitTypes.RANDOM_GROUP_2 && isSplitBranchRoot(child, allSends))
            )
            .forEach((child) => processSend(child, nodeId, context));
    }
};

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Builds a canonical graph structure from a flat sends array.
 *
 * Works for both draft sends (parentIndex-based, no id) and published structure items
 * (parentId-based, with id). An optional fullSends array can be supplied so the builder
 * can look up segmentDefinition / segments for conditional split nodes (published flow).
 *
 * @param sends         Flat array of CampaignSend (draft) or CampaignStructureItem (published)
 * @param fullSends     Optional full Send objects for segment data lookup (published only)
 * @param abTestConfig  Optional A/B test configuration — inserts an ab_test node when present
 */
const buildCampaignGraph = (
    sends: (CampaignSend | CampaignStructureItem)[],
    fullSends?: Send[],
    abTestConfig?: ABTestConfig
): CampaignGraph => {
    const nodes: GraphNode[] = [];
    const edges: GraphEdge[] = [];
    const visitedNodeIds = new Set<string>();

    if (!sends || sends.length === 0) {
        return { nodes, edges };
    }

    // Cast the heterogeneous input to the broad RawSendInput type once, then
    // attach _index so every item is a fully typed InternalSend.
    // `Send` (published) stores the template id as `emailTemplateId`; normalise it
    // to `templateId` so the rest of the builder can use a single field name.
    const allSends: InternalSend[] = (sends as RawSendInput[]).map((send, index) => ({
        ...send,
        templateId: (send as any).templateId ?? (send as any).emailTemplateId,
        _index: index,
    }));

    // Roots are sends with no parent reference
    const roots = allSends.filter((s) => s.parentIndex == null && s.parentId == null);
    if (roots.length === 0) return { nodes, edges };

    const firstRoot = roots[0];

    // Audience node — always first, keyed to the first root's segment/cohort
    const audienceNode: GraphNode = {
        id: 'audience',
        type: 'audience',
        data: { segmentId: firstRoot.segmentId, cohortId: firstRoot.cohortId },
    };
    nodes.push(audienceNode);
    visitedNodeIds.add('audience');

    const context: BuildContext = { nodes, edges, visitedNodeIds, allSends, fullSends };

    if (abTestConfig) {
        // Insert ab_test node between audience and the root sends
        const abTestNode: GraphNode = {
            id: 'ab-test',
            type: 'ab_test',
            data: { abTestConfig },
        };
        nodes.push(abTestNode);
        visitedNodeIds.add('ab-test');
        edges.push({ id: 'e-audience-ab-test', source: 'audience', target: 'ab-test' });

        roots.forEach((root) => processSend(root, 'ab-test', context));
    } else {
        roots.forEach((root) => processSend(root, 'audience', context));
    }

    return { nodes, edges };
};

export default buildCampaignGraph;
